'use client'

import * as React from 'react'
import type { InstantSearchProps } from 'react-instantsearch'
import { InstantSearch, Configure } from 'react-instantsearch'
import type { UiState } from 'instantsearch.js'
import { useSearchParams } from 'next/navigation'
import { algoliaContentIndex } from 'algolia/algolia-content'
import { defaultSearchClient } from 'algolia/algolia'
import { InsightsMiddleware } from 'algolia/insights-middleware'
import { useRouter, usePathname } from '@repo/nextjs-utils/navigation'

import { useContentSearchCategoryQuery } from '@web/gen/content'
import type { ContentLanguage } from '@web/gen/types'
import { getSearchState, searchStateToQuery } from '@web/components/content-api/search-page/utils'
import { buildAlgoliaColorFilter } from '../utils/build-algolia-color-filter'
import { mapCategoryPageTypeToCategoryDetails } from '@web/components/content-api/config'

export const SearchPageContext = React.createContext<{
  color: string | null
  updateColor: (newColor: string | null) => void
  contentLanguage: ContentLanguage
  categoryName: string
  expanded: string[] | null
}>(null!)

export function useSearchPageContext() {
  const context = React.useContext(SearchPageContext)

  if (!context) {
    throw new Error('useSearchPageContext must be used within a SearchPageContext')
  }

  return context
}

export const ContentSearchPageInstantSearchProvider: React.FC<
  Partial<InstantSearchProps> & {
    authorId: string
    contentLanguage: ContentLanguage
    hitsPerPage?: number
    categoryName: string
  }
> = ({ children, authorId, contentLanguage, hitsPerPage = 50, categoryName }) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchParamsColor = searchParams?.get('color')
  const queryColor = searchParamsColor ? decodeURIComponent(searchParamsColor) : null
  const expanded = searchParams?.get('expanded')
  const [color, setColor] = React.useState(queryColor)

  const isStockPhotos =
    categoryName === mapCategoryPageTypeToCategoryDetails.stockphotos.categoryName

  const { data } = useContentSearchCategoryQuery(
    {
      name: '',
      parentId: mapCategoryPageTypeToCategoryDetails.stockphotos.parentId,
      language: contentLanguage,
    },
    {
      enabled: isStockPhotos,
    },
  )

  const normalizeCategories: Record<string, { id: string; name: string; slug: string }> = {}

  data?.contentSearchCategory?.forEach(element => {
    normalizeCategories[element.id] = element
  })

  const searchState = getSearchState({
    url: searchParams?.toString() ?? '',
    lang: contentLanguage,
    authorId,
    isStockPhotos,
    categories: normalizeCategories,
  })

  React.useEffect(() => {
    const searchParamsString = searchParams.toString()
    const queryParams = new URLSearchParams(searchParamsString)

    if (color) {
      queryParams.set('color', color)
    } else {
      queryParams.delete('color')
    }

    const query = queryParams.toString()

    if (query.length > 0 && searchParamsString !== query) {
      if (typeof window === 'undefined') {
        router.push(`${pathname}?${query}`, {
          scroll: false,
        })
      } else {
        window.history.pushState({}, '', `?${query}`)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color])

  const handleStateChange = React.useCallback(
    ({
      uiState,
      setUiState,
    }: {
      uiState: UiState
      setUiState: (uiState: UiState | ((previousUiState: UiState) => UiState)) => void
    }) => {
      const query = searchStateToQuery({
        searchState: {
          uiState,
          other: {
            expanded: expanded ? expanded : '',
            color: color,
          },
        },
        lang: contentLanguage,
        categories: normalizeCategories,
      })

      const newState = getSearchState({
        url: query,
        lang: contentLanguage,
        authorId,
        isStockPhotos,
        categories: normalizeCategories,
      })

      setUiState(newState.uiState)

      if (typeof window === 'undefined') {
        router.push(`${pathname}?${query}`, {
          scroll: false,
        })
      } else {
        window.history.pushState({}, '', `?${query}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLanguage, color, normalizeCategories],
  )

  const handleColorChange = (newColor: string | null) => {
    setColor(newColor)
  }

  const colorFilters = color ? buildAlgoliaColorFilter(color) : ''

  if (isStockPhotos && !data) {
    return null
  }

  return (
    <InstantSearch
      initialUiState={searchState.uiState}
      onStateChange={handleStateChange}
      searchClient={defaultSearchClient}
      indexName={algoliaContentIndex}
    >
      <Configure
        hitsPerPage={hitsPerPage}
        filters={`authorId:${authorId}${color ? ` AND ${colorFilters}` : ''}`}
      />
      <SearchPageContext.Provider
        value={{
          color: color,
          updateColor: handleColorChange,
          contentLanguage,
          categoryName,
          expanded: expanded ? expanded.split(',') : null,
        }}
      >
        {children}
      </SearchPageContext.Provider>
      <InsightsMiddleware />
    </InstantSearch>
  )
}
